import { useCallback, useEffect, useMemo, useState } from "react";
import {
  SCORE_RULES,
  getDropdown2AnswerScoreDelta,
  getInputAnswerScoreDelta,
  isDropdownAnswerCorrect,
  isDropdown2AnswerCorrect,
  isInputAnswerCorrect,
  isLogicalSequenceAnswerCorrect,
  isMultipleChoiceAnswerCorrect,
  isTestAnswerCorrect,
} from "./scoring";

const DEFAULT_ATTEMPTS = 2;

function getInitialTransientState() {
  return {
    selectedOption: null,
    attempts: DEFAULT_ATTEMPTS,
    feedback: "",
    feedbackVisible: false,
    showCheckButton: true,
    showNextButton: false,
    optionStatuses: {},
    allDropdownsSelected: false,
    dropdownValues: {},
    allInputsFilled: false,
    inputValues: [],
    isGameOver: false,
  };
}

export function useModuleRuntime(questions) {
  const safeQuestions = useMemo(
    () => (Array.isArray(questions) ? questions : []),
    [questions]
  );

  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [state, setState] = useState(getInitialTransientState);

  useEffect(() => {
    setSelectedQuestionIndex(0);
    setScore(0);
    setShowModal(false);
    setState(getInitialTransientState());
  }, [safeQuestions]);

  const closeFeedback = useCallback(() => {
    setState((prev) => ({ ...prev, feedbackVisible: false }));
  }, []);

  const selectOption = useCallback((answerId) => {
    setState((prev) => ({ ...prev, selectedOption: answerId }));
  }, []);

  const toggleOption = useCallback((answerId) => {
    setState((prev) => {
      const selectedOptions = Array.isArray(prev.selectedOption)
        ? prev.selectedOption
        : prev.selectedOption
          ? [prev.selectedOption]
          : [];
      const isSelected = selectedOptions.includes(answerId);

      return {
        ...prev,
        selectedOption: isSelected
          ? selectedOptions.filter((item) => item !== answerId)
          : [...selectedOptions, answerId],
      };
    });
  }, []);

  const setDropdownSelection = useCallback((values) => {
    setState((prev) => ({
      ...prev,
      allDropdownsSelected: true,
      dropdownValues: values || {},
    }));
  }, []);

  const setInputSelection = useCallback((values, allFilled) => {
    setState((prev) => ({
      ...prev,
      allInputsFilled: Boolean(allFilled),
      inputValues: Array.isArray(values) ? values : [],
    }));
  }, []);

  const setGameOver = useCallback((value) => {
    setState((prev) => ({ ...prev, isGameOver: Boolean(value) }));
  }, []);

  const handleScoreUpdate = useCallback((nextScore) => {
    setScore(nextScore);
  }, []);

  const currentQuestion = safeQuestions[selectedQuestionIndex] || null;
  const isLastQuestion = selectedQuestionIndex >= safeQuestions.length - 1;
  const outOfAttempts = state.attempts <= 0;

  const checkTestAnswer = useCallback(() => {
    const isCorrect = currentQuestion?.type === "multipleChoice"
      ? isMultipleChoiceAnswerCorrect(currentQuestion, state.selectedOption)
      : isTestAnswerCorrect(currentQuestion, state.selectedOption);
    const selectedOptions = Array.isArray(state.selectedOption)
      ? state.selectedOption
      : state.selectedOption
        ? [state.selectedOption]
        : [];
    if (!selectedOptions.length) return;

    setState((prev) => {
      const nextAttempts = isCorrect ? prev.attempts : prev.attempts - 1;
      const noAttemptsLeft = nextAttempts <= 0;
      const nextStatuses = selectedOptions.reduce((statuses, optionId) => ({
        ...statuses,
        [optionId]: isCorrect ? "correct" : "incorrect",
      }), prev.optionStatuses);

      return {
        ...prev,
        attempts: nextAttempts,
        feedback: isCorrect ? "correct" : "incorrect",
        feedbackVisible: true,
        showCheckButton: !isCorrect && !noAttemptsLeft,
        showNextButton: isCorrect || noAttemptsLeft,
        optionStatuses: nextStatuses,
      };
    });

    if (isCorrect) {
      setScore((prev) => prev + SCORE_RULES.CORRECT_ANSWER);
    }
  }, [currentQuestion, state.selectedOption]);

  const checkDropdownAnswer = useCallback(() => {
    if (!state.allDropdownsSelected) return;

    const isCorrect = isDropdownAnswerCorrect(currentQuestion, state.dropdownValues);
    setState((prev) => {
      const nextAttempts = isCorrect ? prev.attempts : prev.attempts - 1;
      const noAttemptsLeft = nextAttempts <= 0;

      return {
        ...prev,
        attempts: nextAttempts,
        feedback: isCorrect ? "correct" : "incorrect",
        feedbackVisible: true,
        showCheckButton: !isCorrect && !noAttemptsLeft,
        showNextButton: isCorrect || noAttemptsLeft,
      };
    });

    if (isCorrect) {
      setScore((prev) => prev + SCORE_RULES.CORRECT_ANSWER);
    }
  }, [currentQuestion, state.allDropdownsSelected, state.dropdownValues]);

  const checkDropdown2Answer = useCallback(() => {
    if (!state.allDropdownsSelected) return;

    const isCorrect = isDropdown2AnswerCorrect(currentQuestion, state.dropdownValues);
    const scoreDelta = getDropdown2AnswerScoreDelta(currentQuestion, state.dropdownValues);
    const nextAttempts = isCorrect ? state.attempts : state.attempts - 1;
    const noAttemptsLeft = nextAttempts <= 0;
    const shouldApplyScore = isCorrect || noAttemptsLeft;

    setState((prev) => {
      return {
        ...prev,
        attempts: nextAttempts,
        feedback: isCorrect ? "correct" : "incorrect",
        feedbackVisible: true,
        showCheckButton: !isCorrect && !noAttemptsLeft,
        showNextButton: isCorrect || noAttemptsLeft,
      };
    });

    if (shouldApplyScore) {
      setScore((prev) => prev + scoreDelta);
    }
  }, [currentQuestion, state.allDropdownsSelected, state.attempts, state.dropdownValues]);

  const checkInputAnswer = useCallback(() => {
    if (!state.allInputsFilled) return;

    const isCorrect = isInputAnswerCorrect(currentQuestion, state.inputValues);
    const scoreDelta = getInputAnswerScoreDelta(currentQuestion, state.inputValues);
    const nextAttempts = isCorrect ? state.attempts : state.attempts - 1;
    const noAttemptsLeft = nextAttempts <= 0;
    const shouldApplyScore = isCorrect || noAttemptsLeft;

    setState((prev) => {
      return {
        ...prev,
        attempts: nextAttempts,
        feedback: isCorrect ? "correct" : "incorrect",
        feedbackVisible: true,
        showCheckButton: !isCorrect && !noAttemptsLeft,
        showNextButton: isCorrect || noAttemptsLeft,
      };
    });

    if (shouldApplyScore) {
      setScore((prev) => prev + scoreDelta);
    }
  }, [currentQuestion, state.allInputsFilled, state.inputValues, state.attempts]);

  const checkLogicalSequenceAnswer = useCallback(() => {
    const selectedOrder = Array.isArray(state.selectedOption) ? state.selectedOption : [];
    if (!selectedOrder.length) return;

    const isCorrect = isLogicalSequenceAnswerCorrect(currentQuestion, selectedOrder);
    setState((prev) => {
      const nextAttempts = isCorrect ? prev.attempts : prev.attempts - 1;
      const noAttemptsLeft = nextAttempts <= 0;

      return {
        ...prev,
        attempts: nextAttempts,
        feedback: isCorrect ? "correct" : "incorrect",
        feedbackVisible: true,
        showCheckButton: !isCorrect && !noAttemptsLeft,
        showNextButton: isCorrect || noAttemptsLeft,
      };
    });

    if (isCorrect) {
      setScore((prev) => prev + SCORE_RULES.CORRECT_ANSWER);
    }
  }, [currentQuestion, state.attempts, state.selectedOption]);

  const checkAnswer = useCallback(
    (kind) => {
      if (kind === "dropdown") {
        checkDropdownAnswer();
        return;
      }

      if (kind === "dropdown2") {
        checkDropdown2Answer();
        return;
      }

      if (kind === "input") {
        checkInputAnswer();
        return;
      }

      if (kind === "logicalSequence") {
        checkLogicalSequenceAnswer();
        return;
      }

      checkTestAnswer();
    },
    [checkDropdown2Answer, checkDropdownAnswer, checkInputAnswer, checkLogicalSequenceAnswer, checkTestAnswer]
  );

  const nextQuestion = useCallback(() => {
    setSelectedQuestionIndex((prev) => {
      if (prev >= safeQuestions.length - 1) return prev;
      return prev + 1;
    });
    setState(getInitialTransientState());
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [safeQuestions.length]);

  const finishQuiz = useCallback(() => {
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  return {
    currentQuestion,
    selectedQuestionIndex,
    isLastQuestion,
    outOfAttempts,
    score,
    showModal,
    state,
    selectOption,
    toggleOption,
    closeFeedback,
    setDropdownSelection,
    setInputSelection,
    setGameOver,
    handleScoreUpdate,
    checkAnswer,
    nextQuestion,
    finishQuiz,
    closeModal,
  };
}
